import "../style.css";
import type { Action, StateMsg, Tile } from "./types";
import { renderGame, ui, resetUi, Choice } from "./table";

const app = document.getElementById("app")!;

// ---------- identity ----------
function storage(key: string, val?: string): string | null {
  try {
    if (val !== undefined) localStorage.setItem(key, val);
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function randomToken(): string {
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
}
let token = storage("mj_token") || "";
if (!token) {
  token = randomToken();
  storage("mj_token", token);
}
const savedName = () => storage("mj_name") || "";
// ?name=... pre-fills the player name (handy for testing several tabs).
const nameParam = new URLSearchParams(location.search).get("name");
if (nameParam) {
  storage("mj_name", nameParam.slice(0, 20));
  // ...and a separate identity per name, so several tabs can sit at one table.
  token = (storage("mj_token_" + nameParam) || storage("mj_token_" + nameParam, randomToken())) as string;
}

// ---------- routing ----------
const roomMatch = location.pathname.match(/^\/room\/([a-z0-9]+)/);
const roomId = roomMatch ? roomMatch[1] : null;

let state: StateMsg | null = null;
let ws: WebSocket | null = null;
let connected = false;
let notFound = false;
let deadline = 0;
let errorMsg = "";

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

function send(msg: object) {
  if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
}
function act(a: Action) {
  ui.selected = null;
  ui.riichiMode = false;
  ui.chooser = null;
  send({ t: "act", action: a });
}

function connect() {
  if (!roomId) return;
  const proto = location.protocol === "https:" ? "wss" : "ws";
  ws = new WebSocket(`${proto}://${location.host}/ws?room=${roomId}`);
  ws.onopen = () => {
    connected = true;
    if (savedName()) send({ t: "join", name: savedName(), token });
    render();
  };
  ws.onmessage = (ev) => {
    const msg = JSON.parse(ev.data);
    if (msg.t === "state") {
      const prevStep = state?.game ? `${state.game.phase}/${state.game.turn}/${state.game.claiming}` : "";
      state = msg as StateMsg;
      const g = state.game;
      const step = g ? `${g.phase}/${g.turn}/${g.claiming}` : "";
      if (step !== prevStep || !g?.options) {
        ui.chooser = null;
        ui.riichiMode = false;
        ui.selected = null;
      }
      deadline = msg.timeLeftMs ? Date.now() + msg.timeLeftMs : 0;
      if (g?.event && g.event.seq !== ui.lastEventSeq) {
        if (ui.lastEventSeq !== -1) ui.callout = { ...g.event, until: Date.now() + 1600 };
        ui.lastEventSeq = g.event.seq;
      }
      if (g?.phase !== "result") ui.resultHidden = false;
      render();
    } else if (msg.t === "error" || msg.t === "info") {
      errorMsg = msg.msg;
      setTimeout(() => { errorMsg = ""; render(); }, msg.t === "info" ? 4000 : 2500);
      render();
    }
  };
  ws.onclose = (ev) => {
    connected = false;
    ws = null;
    render();
    // 404 on handshake shows up as an immediate close; check whether the room exists.
    fetch(`/ws?room=${roomId}`).then((r) => {
      if (r.status === 404) {
        notFound = true;
        render();
      } else {
        setTimeout(connect, 1500);
      }
    }).catch(() => setTimeout(connect, 3000));
    void ev;
  };
}

// ---------- rendering ----------
function render() {
  if (!roomId) {
    app.innerHTML = renderHome();
    return;
  }
  if (notFound) {
    app.innerHTML = `<div class="page"><div class="card"><h1>Комната не найдена</h1>
      <p>Возможно, сервер перезапускался. Создайте новую комнату.</p>
      <a class="btn primary" href="/">На главную</a></div></div>`;
    return;
  }
  if (!state || !connected && !state) {
    app.innerHTML = `<div class="page"><div class="card"><p>Подключение…</p></div></div>`;
    return;
  }
  const room = state.room;
  const joined = room.you >= 0;
  if (!joined && !savedName()) {
    app.innerHTML = renderNameForm();
    return;
  }
  if (!state.game) {
    app.innerHTML = renderLobby() + banner();
    return;
  }
  app.innerHTML = renderGame(state.game, room, deadline) + banner() +
    `<button class="bugbtn" data-act="report" title="Сообщить об ошибке">🐞</button>`;
  updateTimer();
}

function banner(): string {
  let s = "";
  if (!connected) s += `<div class="toast warn">Соединение потеряно, переподключаюсь…</div>`;
  if (errorMsg) s += `<div class="toast">${esc(errorMsg)}</div>`;
  return s;
}

function renderHome(): string {
  return `<div class="page"><div class="card">
    <h1><span class="logo">中</span> Маджонг Риичи</h1>
    <p class="muted">Правила Mahjong Soul: ханчан, красные пятёрки, открытое таняо.</p>
    <label>Ваше имя<input id="name" maxlength="20" value="${esc(savedName())}" placeholder="Имя"></label>
    <button class="btn primary" data-act="create">Создать комнату</button>
    <p class="muted small">После создания отправьте ссылку на комнату друзьям. Пустые места займут боты.</p>
  </div></div>`;
}

function renderNameForm(): string {
  return `<div class="page"><div class="card">
    <h1><span class="logo">中</span> Комната ${esc(roomId!)}</h1>
    <label>Ваше имя<input id="name" maxlength="20" placeholder="Имя" autofocus></label>
    <button class="btn primary" data-act="join">Войти</button>
  </div></div>`;
}

function renderLobby(): string {
  const room = state!.room;
  const link = `${location.origin}/room/${room.id}`;
  const seats = room.seats.map((s, i) => {
    if (!s) return `<li class="empty">Свободное место</li>`;
    const tags = [s.host ? `<span class="tag">хост</span>` : "", s.bot ? `<span class="tag">бот</span>` : "",
      i === room.you ? `<span class="tag you">вы</span>` : "", !s.online ? `<span class="tag off">офлайн</span>` : ""].join("");
    const kick = room.isHost && !s.host ? `<button class="btn tiny" data-act="kick" data-seat="${i}">✕</button>` : "";
    return `<li><span>${esc(s.name)} ${tags}</span>${kick}</li>`;
  }).join("");
  const full = room.seats.every((s) => s);
  const controls = room.you < 0
    ? `<p class="muted">Все места заняты — вы наблюдатель.</p>`
    : room.isHost
      ? `<div class="row">
          <button class="btn" data-act="addBot" ${full ? "disabled" : ""}>+ Бот</button>
          <button class="btn primary" data-act="start">Начать игру</button>
        </div>
        <p class="muted small">Свободные места при старте займут боты. Места рассаживаются случайно.</p>`
      : `<p class="muted">Ждём, пока хост начнёт игру…</p>`;
  return `<div class="page"><div class="card">
    <h1><span class="logo">中</span> Комната ${esc(room.id)}</h1>
    <label>Ссылка для друзей
      <div class="row"><input readonly value="${esc(link)}" id="link"><button class="btn" data-act="copy">Копировать</button></div>
    </label>
    <ul class="seats">${seats}</ul>
    ${controls}
    <details class="rules"><summary>Правила</summary>
      <ul>
        <li>Ханчан (восточный и южный круги), 25 000 стартовых очков, игра до 30 000 (иначе западный круг).</li>
        <li>Красные пятёрки (по одной на масть), открытое таняо (куитан), кан-дора, ура-дора.</li>
        <li>Двойной рон разрешён, тройной рон — ничья. Абортивные ничьи: 9 терминалов, 4 ветра, 4 риичи, 4 кана.</li>
        <li>Запрет куикаэ. Фуритэн (постоянный, временный, риичи). Нагаси манган. Двойные якуманы.</li>
        <li>Игра заканчивается при уходе в минус.</li>
      </ul>
    </details>
  </div></div>`;
}

function updateTimer() {
  const el = document.getElementById("timer");
  if (!el) return;
  if (!deadline) {
    el.textContent = "";
    return;
  }
  const s = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
  el.textContent = `${s}`;
  el.classList.toggle("low", s <= 5);
}
setInterval(() => {
  updateTimer();
  if (ui.callout && Date.now() > ui.callout.until) {
    ui.callout = null;
    if (state?.game) render();
  }
}, 250);

// ---------- input ----------
const touchMode = window.matchMedia("(hover: none)").matches;

function nameInput(): string | null {
  const el = document.getElementById("name") as HTMLInputElement | null;
  const name = el?.value.trim() || "";
  if (!name) {
    el?.focus();
    return null;
  }
  storage("mj_name", name);
  return name;
}

function choose(c: Choice) {
  act(c.action);
}

app.addEventListener("click", async (e) => {
  const el = (e.target as HTMLElement).closest("[data-act]") as HTMLElement | null;
  if (!el) return;
  const a = el.dataset.act!;
  const g = state?.game;
  const o = g?.options;
  switch (a) {
    case "create": {
      const name = nameInput();
      if (!name) return;
      const r = await fetch("/api/rooms", { method: "POST" });
      const { id } = await r.json();
      location.href = `/room/${id}`;
      return;
    }
    case "join": {
      const name = nameInput();
      if (!name) return;
      send({ t: "join", name, token });
      return;
    }
    case "copy": {
      const inp = document.getElementById("link") as HTMLInputElement;
      try {
        await navigator.clipboard.writeText(inp.value);
        el.textContent = "Скопировано";
      } catch {
        inp.select();
        document.execCommand("copy");
      }
      return;
    }
    case "fullscreen":
      try {
        await document.documentElement.requestFullscreen({ navigationUI: "hide" });
        await (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> }).lock?.("landscape");
      } catch {
        // Not supported (e.g. iOS Safari) or denied: keep the current view.
      }
      return;
    case "report": {
      const text = prompt("Что пошло не так? Опишите коротко: ход и всё состояние стола сохранятся автоматически.");
      if (text === null) return;
      send({ t: "report", text: text.trim() || "(без описания)" });
      return;
    }
    case "addBot": send({ t: "addBot" }); return;
    case "kick": send({ t: "kick", seat: Number(el.dataset.seat) }); return;
    case "start": send({ t: "start" }); return;
    case "lobby": send({ t: "lobby" }); return;
    case "rename": {
      storage("mj_name", "");
      state = state ? { ...state, room: { ...state.room, you: -1 } } : state;
      render();
      return;
    }
  }
  if (!g) return;
  switch (a) {
    case "tile": {
      const t = Number(el.dataset.tile) as Tile;
      const allowed = ui.riichiMode ? o?.riichi : o?.discard;
      if (!allowed || !allowed.includes(t)) return;
      if (touchMode && ui.selected !== t) {
        ui.selected = t;
        render();
        return;
      }
      act({ type: ui.riichiMode ? "riichi" : "discard", tile: t });
      return;
    }
    case "riichi":
      ui.riichiMode = !ui.riichiMode;
      ui.selected = null;
      ui.chooser = null;
      render();
      return;
    case "tsumo": act({ type: "tsumo" }); return;
    case "ron": act({ type: "ron" }); return;
    case "pass": act({ type: "pass" }); return;
    case "kyuushu": act({ type: "kyuushu" }); return;
    case "next": act({ type: "next" }); return;
    case "hideResult": ui.resultHidden = !ui.resultHidden; render(); return;
    case "pon":
    case "chi":
    case "kan": {
      const choices: Choice[] = [];
      if (a === "pon") (o?.pon || []).forEach((tiles) => choices.push({ action: { type: "pon", tiles }, tiles }));
      if (a === "chi") (o?.chi || []).forEach((tiles) => choices.push({ action: { type: "chi", tiles }, tiles }));
      if (a === "kan") {
        if (o?.kan) choices.push({ action: { type: "kan" }, tiles: [] });
        (o?.ankan || []).forEach((t) => choices.push({ action: { type: "ankan", tile: t }, tiles: [t, t, t, t] }));
        (o?.kakan || []).forEach((t) => choices.push({ action: { type: "kakan", tile: t }, tiles: [t] }));
      }
      if (choices.length === 1) choose(choices[0]);
      else {
        ui.chooser = choices;
        render();
      }
      return;
    }
    case "choose": {
      const c = ui.chooser?.[Number(el.dataset.idx)];
      if (c) choose(c);
      return;
    }
    case "cancel":
      ui.chooser = null;
      ui.riichiMode = false;
      render();
      return;
  }
});

app.addEventListener("keydown", (e) => {
  if (e.key !== "Enter") return;
  const t = e.target as HTMLElement;
  if (t.id === "name") {
    (document.querySelector('[data-act="create"],[data-act="join"]') as HTMLElement | null)?.click();
  }
});

resetUi();
render();
connect();
document.addEventListener("fullscreenchange", () => render());

// Send JS errors to the server log, so problems on players' devices are visible.
function reportClientError(msg: string, stack?: string) {
  send({ t: "clientError", text: msg, stack: stack || "" });
}
window.addEventListener("error", (e) => reportClientError(e.message, e.error?.stack));
window.addEventListener("unhandledrejection", (e) => reportClientError(String(e.reason), e.reason?.stack));
